import { z } from 'zod';

/**
 * `defer-debug-vision` — wire contracts for the screenshot vision
 * extractor.
 *
 * Goal: let a manual / QA user point a screenshot at the AI and
 * get back a structured-ish description ("the page shows a login
 * form with the error 'Invalid credentials' under the password
 * field…"). The shape is intentionally narrow: one or more images
 * + a prompt → a text response.
 *
 * Why a separate endpoint:
 *
 *   - The OpenAI / Groq vision call uses `image_url` content parts
 *     in the chat completions API; Anthropic uses a separate
 *     `image` content block with base64 source. Hiding that under
 *     a generic POST keeps the FE simple.
 *   - Vision-capable models are a different set than the text
 *     defaults (e.g. `gpt-4o-mini` for OpenAI, `claude-3-5-sonnet`
 *     for Anthropic, `llama-3.2-90b-vision-preview` for Groq). The
 *     `visionProvider` admin-config field controls which provider
 *     handles the call.
 *
 * v1 scope:
 *
 *   - 1 – 4 images per request. Each image either a fully-qualified
 *     `https://` URL or a `data:image/<png|jpg|webp>;base64,...`
 *     URL. Raw base64 (without the data-URL prefix) is not accepted
 *     since the provider adapters need the MIME type.
 *   - Text-only response (no further tool calls).
 *   - Caller can pin a model and a system prompt.
 *
 * Deferred (v1.1+):
 *
 *   - Multi-turn vision chat (image refinement).
 *   - Structured-output mode (e.g. "extract the visible buttons as
 *     JSON"). Today the caller phrases that as part of the prompt.
 *   - Direct file upload to a Rynora-side blob store with the
 *     vision endpoint reading the bytes — v1 expects the caller to
 *     pass a data URL or a public/signed URL.
 */

/**
 * Hard ceiling for a single `data:` URL — 8 MiB of base64 (~6 MiB
 * of binary) per image. Above that the request fails fast rather
 * than burning provider quota on a payload that will be rejected
 * upstream. Tune via admin config if a real screenshot legitimately
 * exceeds this.
 */
export const VISION_MAX_DATA_URL_BYTES = 8 * 1024 * 1024;

const DataUrlSchema = z
  .string()
  .max(VISION_MAX_DATA_URL_BYTES, `image data URL must be ≤ ${VISION_MAX_DATA_URL_BYTES} bytes`)
  .refine(
    (s) => /^data:image\/(png|jpeg|jpg|webp|gif);base64,[A-Za-z0-9+/=]+$/.test(s),
    'image data URL must be `data:image/<png|jpeg|jpg|webp|gif>;base64,…`',
  );

const HttpsUrlSchema = z
  .string()
  .url()
  .refine((s) => /^https?:\/\//.test(s), 'image URL must be http(s)://…');

/**
 * One input image. `kind` is the discriminator so the adapter can
 * decide whether to embed bytes (Anthropic) or pass the URL through
 * (OpenAI / Groq).
 */
export const LlmVisionImageSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('data-url'), dataUrl: DataUrlSchema }),
  z.object({ kind: z.literal('url'), url: HttpsUrlSchema }),
]);
export type LlmVisionImage = z.infer<typeof LlmVisionImageSchema>;

export const LlmVisionRequestSchema = z.object({
  system: z.string().max(8000).optional(),
  prompt: z.string().min(1).max(16000),
  images: z.array(LlmVisionImageSchema).min(1).max(4),
  model: z.string().max(120).optional(),
  temperature: z.number().min(0).max(2).optional(),
});
export type LlmVisionRequest = z.infer<typeof LlmVisionRequestSchema>;

export const LlmVisionResponseSchema = z.object({
  provider: z.string().min(1),
  model: z.string().min(1),
  text: z.string(),
  usage: z
    .object({
      inputTokens: z.number().int().nonnegative(),
      outputTokens: z.number().int().nonnegative(),
      totalTokens: z.number().int().nonnegative(),
    })
    .optional(),
});
export type LlmVisionResponse = z.infer<typeof LlmVisionResponseSchema>;

/**
 * Pull `media_type` + `base64` out of a `data:` URL. Throws if the
 * string isn't a data URL — call this only after schema validation
 * has accepted the input.
 */
export function parseDataUrl(dataUrl: string): { mediaType: string; base64: string } {
  const m = /^data:(image\/[a-z0-9+.-]+);base64,([A-Za-z0-9+/=]+)$/.exec(dataUrl);
  if (!m) throw new Error('not a base64 data URL');
  // The schema already constrained the media-type to a small whitelist;
  // we re-export whatever the caller wrote so providers (Anthropic) can
  // round-trip the original `image/png` / `image/webp` literal.
  return { mediaType: m[1], base64: m[2] };
}
